import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, X, DollarSign, Globe, Check } from 'lucide-react';
import { NumericFormat } from 'react-number-format';
import { CURRENCIES } from '../../utils/currencyUtils';

interface SettingsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  currency: string;
  powerCost: number;
  onCurrencyChange: (currency: string) => void;
  onPowerCostChange: (cost: number) => void;
}

export function SettingsPopup({
  isOpen,
  onClose,
  currency,
  powerCost,
  onCurrencyChange,
  onPowerCostChange
}: SettingsPopupProps) {
  const selectedCurrency = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];
  const [localCurrency, setLocalCurrency] = React.useState(currency);
  const [localPowerCost, setLocalPowerCost] = React.useState(powerCost);

  React.useEffect(() => {
    setLocalCurrency(currency);
    setLocalPowerCost(powerCost);
  }, [currency, powerCost]);

  const handleConfirm = () => {
    onCurrencyChange(localCurrency);
    onPowerCostChange(localPowerCost);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed top-20 right-4 w-96 z-50"
          >
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Settings className="w-5 h-5 text-blue-500" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Currency Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-400" />
                    Currency
                  </label>
                  <select
                    value={localCurrency}
                    onChange={(e) => setLocalCurrency(e.target.value)}
                    className="w-full px-4 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                  >
                    {CURRENCIES.map((cur) => (
                      <option key={cur.code} value={cur.code}>
                        {cur.symbol} {cur.name} ({cur.code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Power Cost Input */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    Electricity Cost (kWh)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">{selectedCurrency.symbol}</span>
                    </div>
                    <NumericFormat
                      value={localPowerCost}
                      onValueChange={(values) => setLocalPowerCost(values.floatValue || 0)}
                      decimalScale={4}
                      fixedDecimalScale
                      allowNegative={false}
                      className="w-full pl-8 pr-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    Enter your local electricity cost per kilowatt-hour
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Confirm Changes
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}